import { getScopedItem, STORAGE_KEYS } from "../components/common/Helper";
import APIConstants from "./APIConstants";
import APIService from "./APIService";
import AzureAPIService from "./AzureAPIService";
import DebugService from "./DebugService";

/**
 * @description Kubernetes Api Service - Supports both EKS (AWS) and AKS (Azure)
 * 
 * Usage:
 *   - For EKS (AWS):  platform = 'eks'
 *   - For AKS (Azure): platform = 'aks'
 */
const K8sApiService = {

  /**
   * Get all Kubernetes clusters data with pricing and node info
   * @param {string} platform - 'eks' or 'aks'
   * @param {object} data - Contains userAccountId, fromDate, toDate etc.
   * @returns {Promise}
   */
  getClusterData: async (platform = 'eks', data = {}) => {
    try {

      const isAzure = platform === 'aks';
      const Service = isAzure ? AzureAPIService : APIService;

      // Select correct endpoint based on platform
      let url = platform === 'aks' 
        ? APIConstants.ENDPOINT_AZURE_K8S_CLUSTERS_DATA 
        : APIConstants.ENDPOINT_K8S_CLUSTERS_DATA;

      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
        const parseToJson = JSON.parse(userDetails);

        if (data["userAccountId"]) {
          url += `?userAccountId=${data["userAccountId"]}`;
        }
        if (parseToJson["userId"]) {
          url += `&userId=${parseToJson["userId"]}`;
        }
        if (data["fromDate"]) {
          url += `&startMonthDate=${data["fromDate"]}`;
        }
        if (data["toDate"]) {
          url += `&endMonthDate=${data["toDate"]}`;
        }
        if (data["isForceRefresh"]) {
          url += `&isForceRefresh=${data["isForceRefresh"]}`;
        }
      }

      return await Service.get(url);
    } catch (err) {
      DebugService.error(
        `K8s Api Service: Caught error while fetching cluster data for ${platform.toUpperCase()}`,
        err,
      );
    }
  },

  /**
   * Get OpenCost pricing info (supports Pod, Namespace, Node, Service etc.)
   * @param {string} platform - 'eks' or 'aks'
   * @param {object} data 
   * @returns {Promise}
   */
  getNamespaceData: async (platform = 'eks', data = {}) => {
    try {
      const isAzure = platform === 'aks';
      const Service = isAzure ? AzureAPIService : APIService;

      let url = platform === 'aks' 
        ? APIConstants.ENDPOINT_AZURE_K8S_CLUSTERS_NAMESPACE_DATA 
        : APIConstants.ENDPOINT_K8S_CLUSTERS_NAMESPACE_DATA;

      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
        const parseToJson = JSON.parse(userDetails);

        if (parseToJson["userId"]) {
          url += `?userId=${parseToJson["userId"]}`;
        }
        if (data['userAccountId']) {
          url += `&userAccountId=${data["userAccountId"]}`;
        }
        if (data['startMonthDate']) {
          url += `&startMonthDate=${data["startMonthDate"]}`;
        }
        if (data['endMonthDate']) {
          url += `&endMonthDate=${data["endMonthDate"]}`;
        }
        if (data['clusterName']) {
          url += `&clusterName=${data["clusterName"]}`;
        }
        if (data['region']) {
          url += `&region=${data["region"]}`;
        }
        if (data['aggregate']) {
          url += `&aggregate=${data["aggregate"]}`;
        }
        // Extra parameter required only for AKS
        if (platform === 'aks' && data['resourceGroup']) {
          url += `&resourceGroup=${data["resourceGroup"]}`;
        }
      }

      return await Service.get(url, data);
    } catch (err) {
      DebugService.error(
        `K8s Api Service: Caught error while fetching namespace/opencost data for ${platform.toUpperCase()}`,
        err,
      );
    }
  },

  /**
   * Get attached Load Balancer information
   * @param {string} platform - 'eks' or 'aks'
   * @param {object} data 
   * @returns {Promise}
   */
  getLoadBalancerData: async (platform = 'eks', data = {}) => {
    try {

      const isAzure = platform === 'aks';
      const Service = isAzure ? AzureAPIService : APIService;

      // Select correct endpoint based on platform
      const url = platform === 'aks' 
        ? APIConstants.ENDPOINT_AZURE_K8S_CLUSTERS_LOAD_BALANCER_DATA 
        : APIConstants.ENDPOINT_K8S_CLUSTERS_LOAD_BALANCER_DATA;

      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
        const parseToJson = JSON.parse(userDetails);

        if (parseToJson["userId"]) {
          data['userId'] = parseToJson["userId"];
        }
      }

      return await Service.post(url, data);
    } catch (err) {
      DebugService.error(
        `K8s Api Service: Caught error while fetching load balancer data for ${platform.toUpperCase()}`,
        err,
      );
    }
  },

  /**
   * Delete OpenCost URL mapping
   * @param {string} platform - 'eks' or 'aks'
   * @param {number|string} openCostUrlId 
   * @returns {Promise}
   */
  deleteOpenCostUrl: async (platform = 'eks', openCostUrlId) => {
    try {

      const isAzure = platform === 'aks';
      const Service = isAzure ? AzureAPIService : APIService;

      const urlKey = platform === 'aks' 
        ? APIConstants.ENDPOINT_AZURE_K8S_DELETE_OPENCOST_URL 
        : APIConstants.ENDPOINT_K8S_CLUSTERS_DELETE_OPEN_COST;

      let url = `${urlKey}?opencostUrlMappingId=${openCostUrlId}`;

      return await Service.delete(url);
    } catch (err) {
      DebugService.error(
        `K8s Api Service: Error Caught while deleting open cost url for ${platform.toUpperCase()} with id ${openCostUrlId}`,
        err
      );
    }
  },

  /**
   * Manually save / upsert OpenCost URL
   * @param {string} platform - 'eks' or 'aks'
   * @param {object} data 
   * @returns {Promise}
   */
  saveOpenCostUrl: async (platform = 'eks', data = {}) => {
    try {

      const isAzure = platform === 'aks';
      const Service = isAzure ? AzureAPIService : APIService;

      const urlKey = platform === 'aks' 
        ? APIConstants.ENDPOINT_AZURE_K8S_EDIT_OPENCOST_URL 
        : APIConstants.ENDPOINT_K8S_CLUSTERS_EDIT_OPEN_COST;

      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
        const parseToJson = JSON.parse(userDetails);

        if (parseToJson["userId"]) {
          data['userId'] = parseToJson["userId"];
        }
      }

      return await Service.post(urlKey, data);
    } catch (err) {
      DebugService.error(
        `K8s Api Service: Error Caught while saving open cost url for ${platform.toUpperCase()}`,
        data,
        err
      );
    }
  },
};

export default K8sApiService;